import output from "_src/engine/output";
import config from "_src/config";
import { idGenerator } from "_src/utils";
import {propertyType, objectType} from "_src/constants";
import {
  createProperty,
  reformValue,
  generateGroupColor,
} from "_src/reformer/property";
import projectTree from "_src/reformer/project_tree";

export default {
  serializeObjects(json) {
    const cadList = projectTree.getPropertiesByType(objectType.CAD);
    const scanList = projectTree.getPropertiesByType(objectType.SCAN).concat(projectTree.getPropertiesByType(objectType.POINTCLOUD));
    const fusionList = projectTree.getPropertiesByType(objectType.FUSION);
    const photoGroupList = projectTree.getPropertiesByType(objectType.PHOTO_GROUP);
    const groupsList = projectTree.getPropertiesByType(objectType.GROUP);
    const linearMeasureList = projectTree.getPropertiesByType(objectType.LINEAR_MEASURE);
    const geodesicMeasureList = projectTree.getPropertiesByType(objectType.GEODESIC_MEASURE);
    const sectionList = projectTree.getPropertiesByType(objectType.SECTION);
    const distanceMapList = projectTree.getPropertiesByType(objectType.DISTANCE_MAP);

    const serializeObjectList = (properties, jsonSection, serializationFn) => {
      for (const property of properties) {
        const objectJSON = jsonSection.find(object => object["id"] === property.getValue("id"));
        if (!objectJSON) {
          output.error(output.DEVELOPERS_ERROR, "Can't serialize object: " + property.getValue("id") + ". Object doesn't exist.");
          continue;
        }
        serializationFn(property, objectJSON);
      }
    }

    serializeObjectList(scanList, (json["scans"] || []).concat((json["point-clouds"] || [])), serializeObject);
    serializeObjectList(fusionList, json["fusions"], serializeObject);
    serializeObjectList(cadList, json["cad-objects"], serializeObject);
    serializeObjectList(photoGroupList, json["photo-sets"], serializeBaseObject);
    serializeObjectList(linearMeasureList, json["linear-measures"], serializeCommonMeasure);
    serializeObjectList(geodesicMeasureList, json["geodesic-measures"], serializeCommonMeasure);
    serializeObjectList(distanceMapList, json["distance-maps"], serializeCommonMeasure);
    serializeObjectList(sectionList, json["section-measures"], serializeSection);

    if (!("groups" in json) && groupsList.length > 0) {
      json["groups"] = [];
    }

    for (const property of groupsList) {
      let groupJSON = json["groups"].find(group => group["id"] === property.getValue("id"));
      if (!groupJSON) {
        groupJSON = {};
        json["groups"].push(groupJSON);
      }
      serializeGroup(property, groupJSON);
    }
  },

  serializeOrdering(json) {
    json["ordering"] = [];
    for (const item of projectTree.getOrdering()) {
      if (!item) {
        json["ordering"].push(null);
        continue;
      }

      const property = projectTree._getProperty(item["id"]);
      // Objects from AStudio 15+ are not added to WS objects structure
      const uuid = property ? property.getValue("uuid"): item["uuid"];

      json["ordering"].push({
        "id"   : item["id"],
        "uuid" : uuid
      });
    }
  },

  createGroupProperty(groupData, shadesInGroup) {
    const property = createProperty(propertyType.GROUP);

    property.insertValue("name", reformValue(groupData, "name", () => "", "group"));
    property.insertValue("id", reformValue(groupData, "id", () => idGenerator.getNext(), "group"));
    property.insertValue("groupID", reformValue(groupData, "groupid", () => ""));
    property.insertValue("uuid", reformValue(groupData["extra"], "uuid", () => idGenerator.generateUUID()));
    property.insertValue("visible", reformValue(groupData["extra"], "visible", () => true));
    property.insertValue("color", reformValue(groupData["extra"], "color", () => generateGroupColor(shadesInGroup)));
    property.insertValue("locked", reformValue(groupData["extra"], "locked", () => false));

    return property;
  },

}

function serializeBaseObject(property, objectJSON) {
  objectJSON["name"] = property.getValue("name");
  objectJSON["extra"]["visible"] = property.getValue("visible");
}

function serializeObject(property, objectJSON) {
  serializeBaseObject(property, objectJSON);

  objectJSON["transform"] = Array.from(property.getValue("transform"));
  objectJSON["extra"]["color"] = property.getValue("color");
}

function serializeSection(property, sectionGroupJSON) {
  serializeCommonMeasure(property, sectionGroupJSON);
  if (!property.getValue("geometries")) return; //section structure is not initialized yet

  let sectionOffset = 0;
  for (const zcut of sectionGroupJSON["cuts-visibility"] || sectionGroupJSON["cuts-info"] || []) {
    const sections = zcut["sections"] || [];
    for (let i=0; i < sections.length; i++) {
      const section = sections[i];
      const sectionProperty = property.getValue("geometries")[sectionOffset + i];
      section["visible"] = sectionProperty["visible"];

      const contours = section["contours"] || [];
      for (let j=0; j < contours.length; j++) {
        const contour = contours[j];
        const contourProperty = sectionProperty["contours"][j];
        contour["visible"] = contourProperty["visible"];
      }
    }

    sectionOffset += sections.length;
  }
}

function serializeCommonMeasure(property, measureJSON) {
  measureJSON["extra"]["visible"] = property.getValue("visible");
}

function serializeGroup(property, groupJSON) {
  groupJSON["name"] = property.getValue("name");
  groupJSON["extra"]["color"] = property.getValue("color");
  groupJSON["extra"]["visible"] = property.getValue("visible");
}

export function initGeometries(geometries) {
  const properties = [];
  for (const geometry of geometries) {
    const property = createProperty(propertyType.ASSET);

    property.insertValue("id", reformValue(geometry, "id", () => ""));
    property.insertValue("box", reformValue(geometry, "box", () => [-1,-1,-1,1,1,1]));
    property.insertValue("grid", reformValue(geometry, "grid", () => [-1,-1,-1]));
    property.insertValue("name", reformValue(geometry, "name", () => ""));
    property.insertValue("attrs", reformValue(geometry, "vertex-attrs", () => null));

    const levels = property.getValue("levels");

    for (const level of geometry["levels"]) {

      const levelProperty = createProperty(propertyType.LEVEL);

      levelProperty.insertValue("id", reformValue(level, "id", () => ""));
      levelProperty.insertValue("name", reformValue(level, "name", () => ""));
      levelProperty.insertValue("index", reformValue(level, "index", () => 0));
      levelProperty.insertValue("points", reformValue(level, "points", () => 0));
      levelProperty.insertValue("triangles", reformValue(level, "triangles", () => 0));
      levelProperty.insertValue("file", reformValue(level, "file", () => ""));
      levelProperty.insertValue("hash", geometry["hash"] || (geometry["box"] + level["points"] + level["triangles"]));

      levels.push(levelProperty);
    }
    properties.push(property);
  }
  return properties;
}

export function initTextures(textures) {
  const properties = [];

  if (config.dropTextures) {
    return properties;
  }
  for (const texture of textures) {
    const property = createProperty(propertyType.ASSET);

    const levels = property.getValue("levels");

    for (const level of texture["levels"]) {

      const levelProperty = createProperty(propertyType.LEVEL);

      levelProperty.insertValue("width", reformValue(level, "width", () => 1));
      levelProperty.insertValue("height", reformValue(level, "height", () => 1));
      levelProperty.insertValue("channels", reformValue(level, "channels", () => 3));
      levelProperty.insertValue("index", reformValue(level, "index", () => 0));
      levelProperty.insertValue("file", reformValue(level, "file", () => ""));
      levelProperty.insertValue("hash", level["hash"] || level["file"]);

      levels.push(levelProperty);
    }
    properties.push(property);
  }
  return properties;
}
